/* eslint-disable jsx-a11y/no-static-element-interactions, react/prop-types */
import React, { useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { HeadingText, Link } from '@paypalcorp/pp-react';

// Helpers.
import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';

import { trackLink } from '../../lib/analytics-helper';

// Constants
import { POPULAR_ARTICLE } from '../constants/PropTypesShapes';
import { getBNATitle } from '../../lib/messageHelper';

// Actions.
import updateBnaData from '../actions/action-update-bna-data';

// Containers
import InteractiveGuide from './InteractiveGuideContainer';
import { articleChannelTypes, CC3Channel } from '../../common/commonConstants';
import { setContextApiCall, updateContext } from '../../contactUs/actions/action-article';
import { guideClicked } from '../actions/action-icf-page';
import { filterRecommendedChannels } from '../../lib/globalHelper';
import {
  ECM_COMPONENT_IDS_DEFAULT_VALUE,
  ECM_COMPONENT_IDS_KEY,
  ECM_DOC_ID_DEFAULT_VALUE,
  ECM_DOC_ID_KEY,
} from '../../contactUs/constants/ecmFPTITagsKeys';

const BNAChannel = 'BNA';
const CUSTOMER_INTENT_DISPUTE = 'Dispute';
const CUSTOMER_INTENT_REFUND = 'Refund';

const PopularArticles = function (props) {
  const { articles, error, isLoading, isNetNewAccount, disputes, refunds } = props;
  const popular = {
    common: i18n.message('smarthelp/home.home.common'),
    recommended: i18n.message('smarthelp/home.home.recommended'),
    errorMessage: i18n.message('smarthelp/Error.error.popular'),
    recommendedTile: i18n.message('smarthelp/contactOption.recommend'),
    nnaSubTitle: i18n.message('smarthelp/home.home.NNA.welcomeSetupGuideMessage'),
    getStarted: i18n.message('smarthelp/home.home.getStarted'),
  };

  const dispatch = useDispatch();

  /**
   * Function to pass the intent based on first FAQ in recommended section or based on tracker
   *  - Dispute tracker (if present)
   *  - Refund tracker (if present) (if both trackers, prioritize dispute)
   *  - Else first FAQ mapped to common questions
   */
  const setContextWithTrackersOrFAQData = () => {
    let setContextTrackersData = {};
    const setContextTrackersDataIntent = [];

    if (Array.isArray(disputes) && disputes.length > 0) {
      setContextTrackersDataIntent.push({ customer_intent: CUSTOMER_INTENT_DISPUTE });
      setContextTrackersData = disputes[0];
      // fire the setContext with disputes data
      dispatch(setContextApiCall(setContextTrackersDataIntent, '', false, true, setContextTrackersData));
    } else if (Array.isArray(refunds) && refunds.length > 0) {
      setContextTrackersDataIntent.push({ customer_intent: CUSTOMER_INTENT_REFUND });
      setContextTrackersData = refunds[0];
      // fire the setContext with refunds data
      dispatch(setContextApiCall(setContextTrackersDataIntent, '', false, true, setContextTrackersData));
    } else if (
      Array.isArray(articles) &&
      articles.length > 0 &&
      Array.isArray(articles[0].tags) &&
      articles[0].tags.length > 0 &&
      Array.isArray(articles[0].tags[0].predictions) &&
      articles[0].tags[0].predictions.length > 0
    ) {
      // pass the intent and fire the setContext with data from first FAQ mapped to recommended answers
      const firstFAQ = articles[0];
      const faqId = firstFAQ.articleId;
      const intents = firstFAQ.tags[0].predictions || [];
      const customerIntent = intents.map(({ intent, score }) => ({ customer_intent: intent, score }));
      // get the ECM tags from article data
      const fptiDocId = firstFAQ[ECM_DOC_ID_KEY] || ECM_DOC_ID_DEFAULT_VALUE;
      const fptiComponentIds = firstFAQ[ECM_COMPONENT_IDS_KEY] || ECM_COMPONENT_IDS_DEFAULT_VALUE;
      // setContextApiCall(intents, faq, customerConfirmation = false, isTrackersData = false, trackersData = {}, fptiDocId = '', fptiComponentIds = '')
      dispatch(setContextApiCall(customerIntent, faqId, false, false, {}, fptiDocId, fptiComponentIds));
    } else {
      // invoke setContext call without valid params to fire the set-context not fired fpti
      dispatch(setContextApiCall());
    }
  };

  useEffect(() => {
    if (Page.isLoggedIn() && !isLoading) {
      setContextWithTrackersOrFAQData();
    }
  }, [isLoading]);

  const goToArticlePage = (currentArticle) => {
    if (window.localStorage) {
      window.localStorage.setItem('isHome', true);
    }
    let link = `smarthelp-common-questions|${currentArticle.articleId}`;
    if (currentArticle.channel === BNAChannel) {
      updateBnaData(currentArticle);
      link = `${link}|${BNAChannel}`;
    }

    // if article Channel is OMNI triggering update context
    if (currentArticle.channel === articleChannelTypes.OMNI_CHANNEL) {
      // Appending FPTI link with OMNI
      link = `${link}|${articleChannelTypes.OMNI_CHANNEL}`;
      updateContext(currentArticle, '', true);
    }

    trackLink({ id: 'homePage', link });
  };

  const handleKeyPress = (event, article) => {
    if (event.which === 13) {
      goToArticlePage(article);
    }
  };

  const renderPromptICF = (c3ListItems, listItems, omniListItems) => (
    <div>
      {c3ListItems ? (
        <div className="merchant-questions">
          <ul>{c3ListItems}</ul>
        </div>
      ) : (
        <div />
      )}
      <InteractiveGuide icfPosition="top" />
      {omniListItems ? (
        <div className="merchant-questions">
          <ul>{omniListItems}</ul>
        </div>
      ) : (
        <div />
      )}
      {listItems ? (
        <div className="popular-questions">
          <ul>{listItems}</ul>
        </div>
      ) : (
        <div />
      )}
    </div>
  );

  // Common function to get parsed article item.
  const getParsedArticleListItem = (article) => (
    <li key={article.articleId} className="popular-title-list">
      <Link
        theme="v2"
        key={article.articleId}
        className="popular-item"
        href={Page.getFullArticleURL(article)}
        onClick={() => goToArticlePage(article)}
        onKeyPress={(event) => handleKeyPress(event, article)}
      >
        {article.articleTitle}
      </Link>
    </li>
  );

  /**
   * Render BNA articles section only if BNA articles exists.
   * Added C3 Articles at the top
   *
   * @param c3Articles
   * @param bnaArticles
   * @returns - BNA Section HTML code
   */
  const renderBnaSection = (c3Articles, bnaArticles, omniArticles) => {
    const isC3ArticlesExist = Array.isArray(c3Articles) && c3Articles.length;
    const isBnaArticlesExist = Array.isArray(bnaArticles) && bnaArticles.length;
    const isOmniArticlesExist = Array.isArray(omniArticles) && omniArticles.length;

    // Only if articles with channel CC3 or channel BNA Exist
    if (isC3ArticlesExist || isBnaArticlesExist || isOmniArticlesExist) {
      const c3ListItems = isC3ArticlesExist ? c3Articles.map((article) => getParsedArticleListItem(article)) : null;
      const bnaListItems = isBnaArticlesExist ? bnaArticles.map((article) => getParsedArticleListItem(article)) : null;
      const omniListItems = isOmniArticlesExist
        ? omniArticles.map((article) => getParsedArticleListItem(article))
        : null;
      return (
        <div>
          <div className="pcc-user-recommended-title">
            <HeadingText theme="v2" size="sm" className="popular-title">
              {getBNATitle()}
            </HeadingText>
            <span className="pp-vx-sh-tag pp-vx-sh-tag--success">{popular.recommendedTile}</span>
          </div>
          {renderPromptICF(c3ListItems, bnaListItems, omniListItems)}
        </div>
      );
    }
    return null;
  };

  /**
   * Render FAQ section
   *
   * @param faqArticles
   * @returns - FAQ section HTML code
   */
  const renderFaqSection = (faqArticles) => {
    if (Array.isArray(faqArticles) && faqArticles.length) {
      const listItems = faqArticles.map((article) => getParsedArticleListItem(article));
      return (
        <div>
          <HeadingText theme="v2" size="sm" className="popular-title">
            {popular.common}
          </HeadingText>
          <div className="popular-questions">
            <ul>{listItems}</ul>
          </div>
        </div>
      );
    }
    return null;
  };

  // ------------------- START OF NNA [NET NEW ACCOUNT] -------------------------------
  // Render articles section below NNA widget.
  // Al the ICF and articles will be displayed in one section.
  const renderNNARecommendationSection = () => {
    const listItems = articles.map((article) => getParsedArticleListItem(article));
    return (
      <div>
        <HeadingText theme="v2" size="sm" className="popular-title">
          {popular.recommended}
        </HeadingText>
        <div className="popular-questions">
          <InteractiveGuide icfPosition="top" />
          <ul>{listItems}</ul>
        </div>
      </div>
    );
  };

  // -------------------- END OF NNA [NET NEW ACCOUNT] --------------------------------

  const renderContents = () => {
    if (error) {
      return <div className="popular-error">{popular.errorMessage}</div>;
    }

    Page.setCanonicalTag({ name: 'home' });

    if (Array.isArray(articles) && articles.length > 0) {
      // IF NNA is enabled render all the articles in one section
      if (isNetNewAccount) {
        return <div>{renderNNARecommendationSection()}</div>;
      }

      // Fallback
      const BnaArticles = articles.filter((article) => article.channel === BNAChannel);
      const omniArticles = filterRecommendedChannels(articles, articleChannelTypes.OMNI_CHANNEL);
      const faqArticles = articles.filter(
        (article) =>
          article.channel !== BNAChannel &&
          article.channel !== CC3Channel &&
          article.channel !== articleChannelTypes.OMNI_CHANNEL,
      );
      const c3Articles = articles.filter((article) => article.channel === CC3Channel);

      if (Page.isLoggedIn()) {
        return (
          <div>
            {renderBnaSection(c3Articles, BnaArticles, omniArticles)}
            {renderFaqSection(faqArticles)}
          </div>
        );
      }
      return renderFaqSection(articles);
    }
    return null;
  };

  return <div className="popular-articles popular-articles__grid">{renderContents()}</div>;
};

function mapStateToProps(state) {
  return {
    articles: state.popular.articles,
    error: state.popular.error,
    isLoading: state.popular.isLoading,
    isNetNewAccount: state.popular.isNetNewAccount,
    disputes: state.disputeTransactions.disputes,
    refunds: state.trackers.transactions,
  };
}

PopularArticles.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.shape(POPULAR_ARTICLE)),
  error: PropTypes.bool,
  isLoading: PropTypes.bool,

  // Method Prop Types.
  isNetNewAccount: PropTypes.bool.isRequired,
};

PopularArticles.defaultProps = {
  articles: [],
  error: false,
  isLoading: true,
  isNetNewAccount: false,
  disputes: [],
  refunds: [],
};

// Dispatch the Action as Props.
function mapDispatchToProps(dispatch) {
  return bindActionCreators({ guideClicked }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(PopularArticles);
